import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["checkbox", "dropdown", "customCheckbox", "maxNotification"]
  static values = {
    storageKey: { type: String, default: "timesheet_visible_columns" },
    customStorageKey: { type: String, default: "timesheet_custom_columns" },
    maxCustomColumns: { type: Number, default: 5 },
  }

  connect() {
    this.loadFromStorage()
    this.loadCustomColumnsFromStorage()
    this.applyVisibility()
    this.applyCustomColumnVisibility()

    // Use MutationObserver to detect when new rows are added (e.g., via ActionCable broadcasts)
    // and re-apply column visibility to ensure consistent display
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          // Check if any added nodes contain data-column elements
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.hasAttribute?.("data-column") || node.querySelector?.("[data-column]")) {
                this.applyVisibility()
                this.applyCustomColumnVisibility()
                return // Only need to apply once per batch of mutations
              }
            }
          }
        }
      }
    })

    // Observe the document body for changes
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    })
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  toggle(event) {
    const columnKey = event.target.value
    const isVisible = event.target.checked

    this.setColumnVisibility(columnKey, isVisible)
    this.saveToStorage()
  }

  loadFromStorage() {
    const stored = localStorage.getItem(this.storageKeyValue)
    if (stored) {
      this.visibleColumns = JSON.parse(stored)
    } else {
      // Default: use data-default attribute from checkboxes
      this.visibleColumns = this.checkboxTargets
        .filter((cb) => cb.dataset.default === "true")
        .map((cb) => cb.value)
    }

    // Update checkboxes to reflect stored state
    this.checkboxTargets.forEach((cb) => {
      cb.checked = this.visibleColumns.includes(cb.value)
    })
  }

  saveToStorage() {
    const visible = this.checkboxTargets.filter((cb) => cb.checked).map((cb) => cb.value)
    localStorage.setItem(this.storageKeyValue, JSON.stringify(visible))
    this.visibleColumns = visible
  }

  setColumnVisibility(columnKey, isVisible) {
    const columns = document.querySelectorAll(`[data-column="${columnKey}"]`)
    columns.forEach((col) => {
      col.classList.toggle("hidden", !isVisible)
      // For status column, also toggle flex class for proper centering
      if (columnKey === "status") {
        col.classList.toggle("flex", isVisible)
      }
    })

    // Also toggle table headers
    const headers = document.querySelectorAll(`th[data-column="${columnKey}"]`)
    headers.forEach((header) => {
      header.classList.toggle("hidden", !isVisible)
    })
  }

  applyVisibility() {
    this.checkboxTargets.forEach((cb) => {
      this.setColumnVisibility(cb.value, cb.checked)
    })
  }

  toggleDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.toggle("hidden")
    }
  }

  closeDropdown(event) {
    if (this.hasDropdownTarget && !this.element.contains(event.target)) {
      this.dropdownTarget.classList.add("hidden")
    }
  }

  // ============================================
  // Custom Columns Support
  // ============================================

  toggleCustom(event) {
    const columnKey = event.target.value
    const isVisible = event.target.checked

    // If trying to enable, check if we've reached the max limit
    if (isVisible) {
      const currentCount = this.getEnabledCustomColumnsCount()
      if (currentCount >= this.maxCustomColumnsValue) {
        // Revert the checkbox
        event.target.checked = false
        this.showMaxNotification()
        return
      }
    }

    this.hideMaxNotification()
    this.setColumnVisibility(columnKey, isVisible)
    this.saveCustomColumnsToStorage()
  }

  loadCustomColumnsFromStorage() {
    const stored = localStorage.getItem(this.customStorageKeyValue)
    if (stored) {
      this.visibleCustomColumns = JSON.parse(stored)
    } else {
      // Default: no custom columns enabled
      this.visibleCustomColumns = []
    }

    // Update checkboxes to reflect stored state
    if (this.hasCustomCheckboxTarget) {
      this.customCheckboxTargets.forEach((cb) => {
        cb.checked = this.visibleCustomColumns.includes(cb.value)
      })
    }
  }

  saveCustomColumnsToStorage() {
    if (!this.hasCustomCheckboxTarget) return

    const visible = this.customCheckboxTargets.filter((cb) => cb.checked).map((cb) => cb.value)
    localStorage.setItem(this.customStorageKeyValue, JSON.stringify(visible))
    this.visibleCustomColumns = visible
  }

  applyCustomColumnVisibility() {
    if (!this.hasCustomCheckboxTarget) return

    this.customCheckboxTargets.forEach((cb) => {
      this.setColumnVisibility(cb.value, cb.checked)
    })
  }

  getEnabledCustomColumnsCount() {
    if (!this.hasCustomCheckboxTarget) return 0

    return this.customCheckboxTargets.filter((cb) => cb.checked).length
  }

  showMaxNotification() {
    if (this.hasMaxNotificationTarget) {
      this.maxNotificationTarget.classList.remove("hidden")
      // Auto-hide after 3 seconds
      setTimeout(() => {
        this.hideMaxNotification()
      }, 3000)
    }
  }

  hideMaxNotification() {
    if (this.hasMaxNotificationTarget) {
      this.maxNotificationTarget.classList.add("hidden")
    }
  }

  // Get all enabled custom columns (for external use, e.g., by table rendering)
  getEnabledCustomColumns() {
    return this.visibleCustomColumns || []
  }
}
